import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import AptiviewLoader from '../../../common/AptiviewLoader/AptiviewLoader';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import api from '../../../../api/client';
import { useWhatsAppSettingsContext } from './WhatsAppSettingsContext';
import {
  CANONICAL_EVENTS,
  TemplatePayloadCaption,
  getProviderLabel,
  hasProviderConfigured,
} from './whatsappTemplateShared';

const FONT = { fontFamily: "'Poppins', sans-serif" };
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';
const FONT_SX = {
  ...FONT,
  '& .MuiTypography-root, & .MuiButton-root, & .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiChip-label, & .MuiInputLabel-root, & .MuiInputBase-input, & .MuiFormHelperText-root, & .MuiAlert-message, & .MuiDialogTitle-root': FONT,
};

const BTN_SX = {
  ...FONT,
  borderRadius: 2.5,
  textTransform: 'none',
  fontWeight: 700,
};

const PRIMARY_BTN_SX = {
  ...BTN_SX,
  background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
  boxShadow: '0 8px 20px rgba(14,165,233,0.2)',
  '&:hover': {
    background: 'linear-gradient(135deg, #0284c7 0%, #c026d3 100%)',
    boxShadow: '0 10px 22px rgba(14,165,233,0.26)',
  },
};

const CARD_SX = {
  borderRadius: 2.5,
  border: '1px solid #e2e8f0',
  boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
};

const OUTLINE_BTN_SX = {
  ...BTN_SX,
  borderColor: '#cbd5e1',
  color: '#334155',
  '&:hover': {
    borderColor: '#94a3b8',
    backgroundColor: '#f8fafc',
  },
};

const emptyEditor = () => ({
  id: null,
  name: '',
  body_text: '',
  external_template_name: '',
  language: 'en',
  content_sid: '',
  namespace: '',
  campaign_id: '',
  is_active: false,
});

function formatApiError(err, fallback) {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.detail) return String(data.detail);
  if (data.error) return String(data.error);
  const parts = [];
  Object.entries(data).forEach(([key, val]) => {
    if (Array.isArray(val)) parts.push(`${key}: ${val[0]}`);
    else if (typeof val === 'string') parts.push(val);
  });
  return parts.length ? parts.join(' ') : fallback;
}

export default function WhatsAppTemplates() {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const {
    companyId,
    companyLoading,
    connectionReady,
    serverSettings,
    effectiveSettings,
    featureDisabled,
    loadError,
    saveConnection,
  } = useWhatsAppSettingsContext();

  const activeProvider = effectiveSettings?.active_provider || null;

  const [selectedEvent, setSelectedEvent] = useState(CANONICAL_EVENTS[0].id);
  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  usePageLoading(companyLoading, {
    message: t('whatsappSettings.loading', 'Loading WhatsApp settings…'),
  });
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(emptyEditor());
  const [editorOpen, setEditorOpen] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchTemplates = useCallback(async () => {
    if (!activeProvider || !selectedEvent || !companyId) return;
    setLoadingList(true);
    try {
      const { data } = await api.get('/whatsapp-templates/by_event/', {
        params: {
          event_type: selectedEvent,
          provider: activeProvider,
          company_id: companyId,
        },
      });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err, t('whatsappTemplates.messages.loadFailed')));
      setTemplates([]);
    } finally {
      setLoadingList(false);
    }
  }, [activeProvider, selectedEvent, companyId, t]);

  useEffect(() => {
    if (connectionReady && activeProvider && companyId) {
      fetchTemplates();
    }
  }, [connectionReady, activeProvider, selectedEvent, companyId, fetchTemplates]);

  const startNew = () => {
    setEditor(emptyEditor());
    setEditorOpen(true);
    setFormError('');
  };

  const openEdit = (tpl) => {
    setEditor({
      id: tpl.id,
      name: tpl.name || '',
      body_text: tpl.body_text || '',
      external_template_name: tpl.external_template_name || '',
      language: tpl.language || 'en',
      content_sid: tpl.content_sid || '',
      namespace: tpl.namespace || '',
      campaign_id: tpl.campaign_id || '',
      is_active: tpl.is_active,
    });
    setEditorOpen(true);
    setFormError('');
  };

  const validateForm = () => {
    if (!editor.name.trim()) return t('whatsappTemplates.validation.nameRequired');
    if (!editor.body_text.trim()) return t('whatsappTemplates.validation.bodyRequired');
    if (activeProvider === 'twilio' && !editor.content_sid.trim()) {
      return t('whatsappTemplates.validation.contentSidRequired');
    }
    return '';
  };

  const handleSave = async () => {
    if (!activeProvider || !companyId) return;
    const validationMsg = validateForm();
    if (validationMsg) {
      setFormError(validationMsg);
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const payload = {
        company_id: companyId,
        provider: activeProvider,
        event_type: selectedEvent,
        name: editor.name.trim(),
        body_text: editor.body_text.trim(),
        external_template_name: editor.external_template_name.trim() || editor.name.trim(),
        language: editor.language.trim() || 'en',
        content_sid: editor.content_sid.trim(),
        namespace: editor.namespace.trim(),
        campaign_id: editor.campaign_id.trim(),
      };
      if (editor.id) {
        await api.put(`/whatsapp-templates/${editor.id}/`, payload);
        toast.success(t('whatsappTemplates.messages.saved'));
      } else {
        await api.post('/whatsapp-templates/', payload);
        toast.success(t('whatsappTemplates.messages.created'));
      }
      await fetchTemplates();
      setEditor(emptyEditor());
      setEditorOpen(false);
    } catch (err) {
      const msg = formatApiError(err, t('whatsappTemplates.messages.saveFailed'));
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (id) => {
    try {
      await api.post(`/whatsapp-templates/${id}/set_active/`, { company_id: companyId });
      toast.success(t('whatsappTemplates.messages.activated'));
      await fetchTemplates();
      if (editor.id === id) setEditor((p) => ({ ...p, is_active: true }));
    } catch (err) {
      toast.error(formatApiError(err, t('whatsappTemplates.messages.activateFailed')));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('whatsappTemplates.messages.deleteConfirm'))) return;
    try {
      await api.delete(`/whatsapp-templates/${id}/`, { params: { company_id: companyId } });
      toast.success(t('whatsappTemplates.messages.deleted'));
      if (editor.id === id) {
        setEditor(emptyEditor());
        setEditorOpen(false);
      }
      await fetchTemplates();
    } catch (err) {
      toast.error(formatApiError(err, t('whatsappTemplates.messages.deleteFailed')));
    }
  };

  const goToSetup = () => setSearchParams({ tab: 'setup' }, { replace: true });

  const handleSaveConnectionAndStay = async () => {
    const pid = effectiveSettings?.active_provider;
    if (!pid) return;
    const result = await saveConnection({
      enabled: true,
      active_provider: pid,
      sender_phone_e164: effectiveSettings?.sender_phone_e164 || '',
      require_whatsapp_opt_in: effectiveSettings?.require_whatsapp_opt_in !== false,
      event_toggles: effectiveSettings?.event_toggles || {},
      providers: { [pid]: effectiveSettings?.providers?.[pid] || {} },
    });
    if (result.ok) {
      toast.success(t('whatsappSettings.messages.saved'));
    } else {
      toast.error(result.error);
    }
  };

  if (companyLoading) {
    return null;
  }

  if (!companyId) {
    return (
      <p className="p-8 text-center text-sm font-medium text-gray-500" style={FONT}>
        {t('whatsappSettings.messages.noCompany')}
      </p>
    );
  }

  if (featureDisabled) {
    return (
      <Alert severity="error" sx={{ m: 2, ...FONT_SX }}>
        {t('whatsappSettings.messages.featureDisabled')}
      </Alert>
    );
  }

  if (!connectionReady || !activeProvider) {
    const providerOnly = Boolean(activeProvider);
    return (
      <div
        className="mx-auto max-w-4xl rounded-2xl border border-gray-200/50 bg-white/90 p-8 text-center shadow-xl md:p-10"
        style={FONT}
      >
        <h2 className="text-xl font-bold tracking-tight text-gray-900">
          {providerOnly
            ? t('whatsappTemplates.credentialsRequiredTitle', {
                provider: getProviderLabel(activeProvider),
              })
            : t('whatsappTemplates.noProviderTitle')}
        </h2>
        <p className="mt-2 text-sm font-medium text-gray-500">
          {providerOnly
            ? t('whatsappTemplates.credentialsRequiredHint')
            : t('whatsappTemplates.noProviderHint')}
        </p>
        {loadError && (
          <Alert severity="warning" sx={{ mt: 3, mb: 1, textAlign: 'left', ...FONT_SX }}>
            {loadError}
          </Alert>
        )}
        <p className="mt-3 mb-6 text-xs font-medium text-gray-400">
          {t('whatsappTemplates.noProviderSteps')}
        </p>
        <button
          type="button"
          onClick={goToSetup}
          className="inline-flex items-center justify-center rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:opacity-95 hover:shadow-lg"
          style={{ ...FONT, background: GRADIENT }}
        >
          {providerOnly
            ? t('whatsappTemplates.enterCredentials')
            : t('whatsappTemplates.goToConnection')}
        </button>
      </div>
    );
  }

  const isTwilio = activeProvider === 'twilio';
  const needsPersist =
    connectionReady && serverSettings && !hasProviderConfigured(serverSettings);

  return (
    <Box sx={FONT_SX}>
      {needsPersist && (
        <Alert severity="info" sx={{ mb: 2, ...FONT_SX }} action={
          <Button color="inherit" size="small" onClick={handleSaveConnectionAndStay} sx={BTN_SX}>
            {t('whatsappTemplates.saveConnectionNow')}
          </Button>
        }>
          {t('whatsappTemplates.unsavedConnectionHint')}
        </Alert>
      )}

      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid #dbeafe',
          background: 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(217,70,239,0.08) 100%)',
        }}
      >
        <Typography variant="body2" color="#334155" fontWeight={600} sx={FONT}>
          {t('whatsappTemplates.providerNote', { provider: getProviderLabel(activeProvider) })}
        </Typography>
      </Paper>

      <Box sx={{ maxWidth: 1120, mx: 'auto' }}>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...CARD_SX, overflow: 'hidden' }}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Typography sx={{ ...FONT, fontWeight: 800, color: '#0f172a' }}>
                {t('whatsappSettings.eventToggles')}
              </Typography>
            </Box>
            <List dense disablePadding>
              {CANONICAL_EVENTS.map((ev) => (
                <ListItemButton
                  key={ev.id}
                  selected={selectedEvent === ev.id}
                  onClick={() => {
                    setSelectedEvent(ev.id);
                    setEditor(emptyEditor());
                    setEditorOpen(false);
                    setFormError('');
                  }}
                    sx={{
                    ...FONT,
                    px: 1.75,
                    py: 1,
                    borderBottom: '1px solid #f1f5f9',
                    '&.Mui-selected': {
                      backgroundColor: '#ecfeff',
                      borderLeft: '3px solid #06b6d4',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: '#cffafe',
                    },
                  }}
                >
                  <ListItemText
                    primary={t(ev.labelKey)}
                    primaryTypographyProps={{ sx: FONT }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ ...CARD_SX, p: 2.5, minHeight: 360 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography fontWeight={800} color="#0f172a" sx={FONT}>
                {t('whatsappTemplates.library')}
              </Typography>
              <IconButton
                size="small"
                onClick={startNew}
                sx={{
                  color: '#0ea5e9',
                  border: '1px solid #bae6fd',
                  bgcolor: '#f0f9ff',
                  '&:hover': { bgcolor: '#cffafe' },
                }}
                aria-label={t('whatsappTemplates.newTemplate')}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
            {loadingList ? (
              <Box py={6} display="flex" justifyContent="center">
                <AptiviewLoader size="sm" message={t('whatsappTemplates.messages.loading', 'Loading templates…')} />
              </Box>
            ) : templates.length === 0 ? (
              <Box py={6} textAlign="center">
                <Typography variant="body2" color="text.secondary" sx={FONT}>
                  {t('whatsappTemplates.emptyLibrary')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={FONT}>
                  {t('whatsappTemplates.selectOrCreateHint')}
                </Typography>
              </Box>
            ) : (
              <List dense>
                {templates.map((tpl) => (
                  <ListItemButton
                    key={tpl.id}
                    selected={editor.id === tpl.id}
                    onClick={() => openEdit(tpl)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.75,
                      border: '1px solid #e2e8f0',
                      '&.Mui-selected': {
                        backgroundColor: '#eef2ff',
                        borderColor: '#c7d2fe',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: '#e0e7ff',
                      },
                    }}
                  >
                    <ListItemText
                      primary={tpl.name}
                      secondary={tpl.is_active ? t('whatsappTemplates.active') : null}
                      primaryTypographyProps={{ sx: FONT }}
                      secondaryTypographyProps={{ sx: FONT }}
                    />
                    {tpl.is_active && (
                      <Chip
                        size="small"
                        label={t('whatsappTemplates.active')}
                        sx={{
                          ...FONT,
                          ml: 1,
                          fontWeight: 700,
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          border: '1px solid #bbf7d0',
                        }}
                      />
                    )}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

      </Grid>
      </Box>

      <Dialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setFormError('');
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            ...FONT_SX,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            boxShadow: '0 20px 60px rgba(15,23,42,0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            ...FONT,
            fontWeight: 800,
            color: '#0f172a',
            background: 'linear-gradient(135deg, rgba(14,165,233,0.08) 0%, rgba(217,70,239,0.08) 100%)',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          {editor.id ? t('whatsappTemplates.editTemplate') : t('whatsappTemplates.newTemplate')}
        </DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TemplatePayloadCaption eventId={selectedEvent} providerId={activeProvider} t={t} />
          <TextField
            fullWidth
            required
            margin="dense"
            label={t('whatsappTemplates.fields.name')}
            value={editor.name}
            onChange={(e) => setEditor((p) => ({ ...p, name: e.target.value }))}
          />
          {isTwilio ? (
            <TextField
              fullWidth
              required
              margin="dense"
              label={t('whatsappTemplates.fields.contentSid')}
              placeholder="HX..."
              value={editor.content_sid}
              onChange={(e) => setEditor((p) => ({ ...p, content_sid: e.target.value }))}
            />
          ) : (
            <TextField
              fullWidth
              margin="dense"
              label={t('whatsappTemplates.fields.providerTemplateName')}
              value={editor.external_template_name}
              onChange={(e) => setEditor((p) => ({ ...p, external_template_name: e.target.value }))}
              helperText={t('whatsappTemplates.fields.providerTemplateNameHint')}
            />
          )}
          <TextField
            fullWidth
            margin="dense"
            label={t('whatsappTemplates.fields.language')}
            value={editor.language}
            onChange={(e) => setEditor((p) => ({ ...p, language: e.target.value }))}
            sx={{ maxWidth: 160 }}
          />
          <TextField
            fullWidth
            required
            margin="dense"
            multiline
            minRows={6}
            label={t('whatsappTemplates.fields.bodyText')}
            value={editor.body_text}
            onChange={(e) => setEditor((p) => ({ ...p, body_text: e.target.value }))}
            helperText={t('whatsappTemplates.fields.bodyTextHint')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="text"
            onClick={() => {
              setEditorOpen(false);
              setFormError('');
            }}
            sx={{ ...BTN_SX, color: '#64748b' }}
          >
            {t('whatsappTemplates.cancel')}
          </Button>
          {editor.id && (
            <Button
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => handleDelete(editor.id)}
              sx={{
                ...BTN_SX,
                borderColor: '#fecaca',
                color: '#b91c1c',
                '&:hover': {
                  borderColor: '#fca5a5',
                  backgroundColor: '#fef2f2',
                },
              }}
            >
              {t('whatsappTemplates.delete')}
            </Button>
          )}
          {editor.id && !editor.is_active && (
            <Button
              variant="outlined"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleSetActive(editor.id)}
              sx={OUTLINE_BTN_SX}
            >
              {t('whatsappTemplates.setActive')}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving}
            onClick={handleSave}
            sx={PRIMARY_BTN_SX}
          >
            {saving ? t('whatsappSettings.saving') : t('whatsappTemplates.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
